import type { ChartClickData } from "../widgets/types/chart"
import { formatChartDateLabel } from "../widgets/utils/chart/dateFormatting"

const CHART_SCOPE_DISPLAY_LABELS = new Set(["entire chart"])

const normalizeLabel = (label: string | null | undefined) => {
  const trimmed = label?.trim()
  return trimmed ? trimmed : null
}

const labelsMatch = (
  first: string | null | undefined,
  second: string | null | undefined,
) =>
  normalizeLabel(first)?.toLowerCase() === normalizeLabel(second)?.toLowerCase()

const getBucketDisplayLabel = (clickData: ChartClickData) => {
  if (clickData.endDate) {
    return null
  }

  const bucketDate = new Date(clickData.bucketTime)
  if (Number.isNaN(bucketDate.getTime())) {
    return null
  }

  if (clickData.bucketInterval) {
    return formatChartDateLabel(bucketDate, clickData.bucketInterval)
  }

  return bucketDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

const getSpecificDisplayLabel = (
  clickData: ChartClickData,
  chartTitle: string | null | undefined,
) => {
  const displayLabel = normalizeLabel(clickData.displayLabel)
  if (!displayLabel) {
    return null
  }

  if (CHART_SCOPE_DISPLAY_LABELS.has(displayLabel.toLowerCase())) {
    return null
  }

  if (labelsMatch(displayLabel, chartTitle)) {
    return null
  }

  return displayLabel
}

export const getDrillDownBreadcrumbLabel = (
  clickData: ChartClickData,
  options: { chartTitle?: string | null } = {},
) => {
  if ((clickData.drillDownScope ?? "point") === "chart") {
    return null
  }

  const specificDisplayLabel = getSpecificDisplayLabel(
    clickData,
    options.chartTitle,
  )
  if (specificDisplayLabel) {
    return specificDisplayLabel
  }

  const labelParts = [
    normalizeLabel(clickData.stringValue),
    normalizeLabel(clickData.splitValue),
    getBucketDisplayLabel(clickData),
  ].filter((part): part is string => Boolean(part))

  return labelParts.length > 0 ? labelParts.join(" · ") : null
}
